import React, { useEffect } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { Mark } from "./ui";
import { CONTACT_EMAIL } from "./constants";
import { SiteNotice } from "../SiteNotice";
import { NotificationBell } from "../NotificationBell";
import { UserMenu } from "../UserMenu";
import { User } from "../../types/api";
import { isPublicSitePath } from "../../utils/publicPaths";

const NAV = [
  { to: "/about", label: "About" },
  { to: "/security", label: "Security" },
  { to: "/roadmap", label: "Roadmap" },
];

// Institutional logos reduced to white-on-transparent "-mono"
const PARTNERS = [
  {
    src: "/logos/hi-mono.svg",
    alt: "University of Iceland",
    href: "https://www.hi.is",
    className: "h-8 sm:h-10",
  },
  {
    src: "/logos/irei-mono.svg",
    alt: "IREI — Icelandic Research e-Infrastructure",
    href: "https://irei.is",
    className: "h-7 sm:h-9",
  },
  {
    src: "/logos/innvidasjodur-mono.png",
    alt: "Innviðasjóður — Icelandic Infrastructure Fund",
    href: "https://www.rannis.is/sjodir/rannsoknir/innvidasjodur/",
    className: "h-11 sm:h-14",
  },
];

interface PublicLayoutProps {
  children: React.ReactNode;
  isAuthenticated: boolean;
  dashboardRoute: string;
  user: User | null;
  isPIAnywhere: boolean;
  hasProjects: boolean;
  isAdmin: boolean;
  onLogout: () => void;
}

// The old per-page /admin tab bar, now a persistent strip shown to anyone who
// administers at least one project — on every page, not just inside /admin.
const ADMIN_NAV = [
  { to: "/admin", label: "Overview" },
  { to: "/admin/users", label: "Users" },
  { to: "/admin/projects", label: "Projects" },
  { to: "/admin/requests", label: "Data Requests" },
  { to: "/admin/datasets", label: "Datasets" },
];

/**
 * Chrome for the whole app: a marketing header (logo, About/Security/Roadmap,
 * login/dashboard) on the public site, and a separate in-app header (logo,
 * admin tabs, notifications, user menu) everywhere else — never both at once.
 */
export const PublicLayout: React.FC<PublicLayoutProps> = ({
  children,
  isAuthenticated,
  dashboardRoute,
  user,
  isPIAnywhere,
  hasProjects,
  isAdmin,
  onLogout,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const isMarketingPage = isPublicSitePath(location.pathname);

  const isAdminNavActive = (path: string) =>
    path === "/admin" ? location.pathname === "/admin" : location.pathname.startsWith(path);

  // Fresh page, fresh scroll position — router does not do this for us. A URL
  // carrying a section anchor (someone sharing a deep link) goes to that
  // section instead; the offset comes from scroll-margin-top in index.css.
  useEffect(() => {
    if (location.hash) {
      const target = document.getElementById(decodeURIComponent(location.hash.slice(1)));
      if (target) {
        target.scrollIntoView();
        return;
      }
    }
    window.scrollTo(0, 0);
  }, [location.pathname, location.hash]);

  return (
    <div className="site min-h-screen flex flex-col bg-sand-50">
      <SiteNotice />
      {isMarketingPage ? (
        <header className="sticky top-0 z-40 border-b border-sand-200 bg-sand-50/90 backdrop-blur">
          <div className="mx-auto flex max-w-6xl items-center gap-4 px-5 py-3.5 sm:px-8">
            <Link to="/" className="flex items-center gap-3 shrink-0">
              <Mark className="h-9 w-9" />
              <span className="leading-tight">
                <span className="block font-display text-lg text-ink">Nafnleyndarkerfi</span>
                <span className="block text-[0.6875rem] uppercase tracking-[0.14em] text-ink-400">
                  Pseudonymization service
                </span>
              </span>
            </Link>

            <nav className="ml-auto hidden items-center gap-1 md:flex">
              {NAV.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={({ isActive }) =>
                    `rounded-md px-3 py-2 text-sm font-medium transition ${
                      isActive
                        ? "bg-white text-ink shadow-[0_1px_2px_rgba(15,46,46,0.06)]"
                        : "text-ink-600 hover:text-ink hover:bg-white/70"
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              ))}
            </nav>

            {isAuthenticated ? (
              <div className="ml-auto flex items-center gap-3 md:ml-3">
                <Link
                  to={dashboardRoute}
                  className="rounded-full border border-ink/15 px-3.5 py-1.5 text-sm font-semibold text-ink transition hover:border-ink/30 hover:bg-white"
                >
                  Dashboard
                </Link>
                <NotificationBell />
                <UserMenu
                  user={user}
                  isPIAnywhere={isPIAnywhere}
                  hasProjects={hasProjects}
                  onLogout={onLogout}
                />
              </div>
            ) : (
              <Link
                to="/login"
                className="ml-auto rounded-lg bg-ink px-4 py-2 text-sm font-semibold text-white transition hover:bg-ink-800 md:ml-3"
              >
                Log in
              </Link>
            )}
          </div>

          {/* Compact nav for narrow screens */}
          <nav className="flex gap-1 overflow-x-auto border-t border-sand-200 px-5 py-2 md:hidden">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  `whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-medium ${
                    isActive ? "bg-white text-ink" : "text-ink-600"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>
        </header>
      ) : (
        <header className="sticky top-0 z-40 border-b border-sand-200 bg-sand-50/90 backdrop-blur">
          <div className="mx-auto flex max-w-6xl items-center gap-4 px-5 py-3.5 sm:px-8">
            <Link to="/" className="flex items-center gap-3 shrink-0">
              <Mark className="h-9 w-9" />
              <span className="leading-tight">
                <span className="block font-display text-lg text-ink">Nafnleyndarkerfi</span>
                <span className="block text-[0.6875rem] uppercase tracking-[0.14em] text-ink-400">
                  Pseudonymization service
                </span>
              </span>
            </Link>

            {isAuthenticated && (
              <div className="ml-auto flex items-center gap-3">
                <NotificationBell />
                <UserMenu
                  user={user}
                  isPIAnywhere={isPIAnywhere}
                  hasProjects={hasProjects}
                  onLogout={onLogout}
                />
              </div>
            )}
          </div>

          {isAdmin && (
            <div className="mx-auto flex max-w-6xl gap-6 overflow-x-auto border-t border-sand-200 px-5 sm:px-8">
              {ADMIN_NAV.map((item) => (
                <button
                  key={item.to}
                  onClick={() => navigate(item.to)}
                  className={`whitespace-nowrap border-b-2 py-3 text-sm font-semibold transition ${
                    isAdminNavActive(item.to)
                      ? "border-accent text-ink"
                      : "border-transparent text-ink-400 hover:text-ink"
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </div>
          )}
        </header>
      )}

      <main className="flex-1">{children}</main>

      {isMarketingPage ? (
      <footer className="mt-20 bg-ink text-sand-100">
        <div className="mx-auto max-w-6xl px-5 py-14 sm:px-8">
          <div className="grid gap-10 sm:grid-cols-2 lg:grid-cols-4">
            <div className="lg:col-span-2">
              <div className="flex items-center gap-3">
                <Mark className="h-8 w-8" />
                <span className="font-display text-lg text-white">Nafnleyndarkerfi</span>
              </div>
              <p className="mt-4 max-w-sm text-sm leading-relaxed text-sand-100/70">
                A pseudonymization service for health research: researchers get the data, nobody
                gets the identities. Built for university departments, institutes, and research
                units — and for the hospitals and registries that supply them with data.
              </p>
            </div>

            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.14em] text-accent-light">
                Read
              </p>
              <ul className="mt-4 space-y-2 text-sm">
                {NAV.map((item) => (
                  <li key={item.to}>
                    <Link to={item.to} className="text-sand-100/80 hover:text-white">
                      {item.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.14em] text-accent-light">
                Access
              </p>
              <ul className="mt-4 space-y-2 text-sm text-sand-100/80">
                <li>
                  <Link to="/login" className="hover:text-white">
                    Log in
                  </Link>
                </li>
                <li>Accounts are issued by invitation only.</li>
                <li>
                  Day-to-day questions: your project&rsquo;s principal investigator, or your
                  department&rsquo;s manager.
                </li>
                <li>
                  Suggestions, bug reports, a new department:{" "}
                  <a href={`mailto:${CONTACT_EMAIL}`} className="underline hover:text-white">
                    {CONTACT_EMAIL}
                  </a>
                </li>
              </ul>
            </div>
          </div>

          <div className="mt-12 flex flex-wrap items-center gap-x-10 gap-y-6 border-t border-white/10 pt-10 sm:gap-x-14">
            {PARTNERS.map((partner) => (
              <a
                key={partner.src}
                href={partner.href}
                target="_blank"
                rel="noreferrer noopener"
                title={partner.alt}
                className="opacity-70 transition-opacity duration-200 hover:opacity-100"
              >
                <img src={partner.src} alt={partner.alt} className={`w-auto ${partner.className}`} />
              </a>
            ))}
          </div>

          <div className="mt-10 flex flex-col gap-2 border-t border-white/10 pt-6 text-xs text-sand-100/50 sm:flex-row sm:items-center sm:justify-between">
            <p>
              In active development — See the{" "}
              <Link to="/roadmap" className="underline hover:text-white">
                roadmap
              </Link>{" "}
              for current status.
            </p>
            <p>Pseudonymization is a technical control, not a substitute for ethics approval.</p>
          </div>
        </div>
      </footer>
      ) : (
      <footer className="bg-white border-t border-gray-200 mt-auto">
        <div className="max-w-7xl mx-auto px-4 py-6 text-center text-gray-600">
          <p className="font-medium">Pseudonymization Service</p>
          <p className="text-sm mt-1">
            Secure context-aware data anonymization for research purposes
          </p>
          <nav className="mt-3 flex justify-center gap-5 text-sm">
            <Link to="/about" className="text-primary hover:underline">
              About &amp; guide
            </Link>
            <Link to="/security" className="text-primary hover:underline">
              Security
            </Link>
            <Link to="/roadmap" className="text-primary hover:underline">
              Roadmap
            </Link>
          </nav>
        </div>
      </footer>
      )}
    </div>
  );
};
